import React, { useRef, useState, useEffect } from "react";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import axios from "axios";
import Header from "./common/Header";
import Preloader from '../components/common/Preloader';
import LoginPopup from "./popup/LoginPopup";
import ReviewModal from "./popup/ReviewModal";
import Footer from "./common/Footer";
import FrontendPageSEO from "../components/common/FrontendPageSEO";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getValidSession } from "./helpers/sessionUtils";
import useFrontendPageContent from "../components/common/useFrontendPageContent";
import { buildVendorProfileUrl } from "../utils/vendorProfileUrl";

const GOOGLE_MAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries = ["places"];
const vendorProfileUrl = (item) => buildVendorProfileUrl(item?.Vendor);

// --- UTILITY: AVAILABILITY PARSER ---
const getFormattedAvailability = (jsonStr) => {
  try {
    if (!jsonStr) return "Contact for Schedule";
    let data = jsonStr;
    if (typeof data === "string") {
      data = JSON.parse(data.startsWith('"') ? JSON.parse(data) : data);
    }
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const todayName = days[new Date().getDay()];
    const schedule = data[todayName];
    if (!schedule || schedule.closed || !schedule.slots?.[0]?.from)
      return `Closed today (${todayName})`;

    const formatTime = (time) => {
      const [hh, mm] = time.split(":");
      const h = parseInt(hh);
      return `${h % 12 || 12}:${mm} ${h >= 12 ? "PM" : "AM"}`;
    };
    return `Open Today: ${formatTime(schedule.slots[0].from)} - ${formatTime(schedule.slots[0].to)}`;
  } catch (e) {
    return "Available by Appointment";
  }
};

const StarRating = ({ avg = 0, count = 0, onClick }) => {
  const fullStars = Math.floor(avg);
  const hasHalf = avg - fullStars >= 0.5;

  return (
    <div
      className="d-flex align-items-center justify-content-center justify-content-lg-start flex-wrap gap-2 cursor-pointer"
      onClick={onClick}
    >
      <div className="d-flex" style={{ gap: "2px" }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <i
            key={star}
            className={`bi ${star <= fullStars ? "bi-star-fill" : star === fullStars + 1 && hasHalf ? "bi-star-half" : "bi-star"} text-warning`}
            style={{ fontSize: "18px" }}
          />
        ))}
      </div>
      <div className="d-flex align-items-baseline gap-1">
        <span className="fw-bold text-dark" style={{ fontSize: "18px" }}>
          {Number(avg).toFixed(1)}/5
        </span>
        <span className="text-muted" style={{ fontSize: "13px" }}>
          ({count} reviews)
        </span>
      </div>
    </div>
  );
};

const slugify = (str) =>
  str
    ? String(str)
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/\-\-+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '')
    : '';

const generateMedicalSlug = (data, type) => {
  const name = slugify(type === 'doctor' ? data.doctor_name : data.hospital_name);
  const city = slugify(data.city);
  const id = data.id;

  return [name, city, id]
    .filter(Boolean)
    .join('-')
    .replace(/--+/g, '-');
};

// --- UPDATED MedicalResultCard ---
const MedicalResultCard = ({ data, type, onReviewClick, onAction, isSubmitting, pendingAction, activeId }) => {
  const isDoctor = type === "doctor";
  const successColor = "#198754";
  const isActive = activeId === data.id && isSubmitting;

  const reviews = data.reviews || [];
  const reviewCount = reviews.length;
  const avgRating =
    reviewCount > 0
      ? reviews.reduce((sum, r) => sum + parseFloat(r.rating || 0), 0) /
      reviewCount
      : data.avg_rating || 0;
  const totalReviews = data.review_count ?? reviewCount;

  const displayImage = isDoctor
    ? data.doctor_images?.[0]?.image_url ||
    "/assets/img/placeholders/doctor.png"
    : data.hospital_images?.[0]?.image_url ||
    "/assets/img/placeholders/hospital.png";

  const name = isDoctor ? data.doctor_name : data.hospital_name;

  const handleNavigate = (e) => {
    if (e.target.closest('.btn') || e.target.closest('.review-section') || e.target.closest('.medical-mobile-action-row')) {
      return;
    }
    const url = `/medical-details/${type}/${generateMedicalSlug(data, type)}`;
    window.open(url, '_blank');
  };

  return (
    <div
      className="card mb-3 shadow-sm rounded-2 bg-white overflow-hidden result-card-hover"
      onClick={handleNavigate}
      style={{
        cursor: 'pointer',
        border: '1px solid #e0e0e0'
      }}
    >
      <div className="row g-0 align-items-stretch medicaltourism-card">

        <div className="col-12 col-md-4 col-lg-3 p-0 d-flex align-items-stretch justify-content-stretch bg-light-subtle">
          <div
            className="overflow-hidden shadow-sm w-100 position-relative medicaltourism-div"
            style={{
              borderColor: '#ddd'
            }}
          >
            <img
              src={displayImage}
              alt={name}
              className="w-100 "
              style={{ objectFit: "cover", display: "block" }}
              onError={(e) => {
                e.target.src = isDoctor
                  ? "/assets/img/placeholders/doctor.png"
                  : "/assets/img/placeholders/hospital.png";
              }}
            />
          </div>
        </div>

        {/* INFO */}
        <div className="col-12 col-md-8 col-lg-6 p-3 text-start">
          <div className="d-flex flex-wrap align-items-center mb-1 overflow-hidden">
            <h4
              className="mb-3 text-truncate-custom"
              style={{ color: '#333', fontSize: '18px', cursor: 'pointer' }}
            >
              {name}
            </h4>
            {!isDoctor && (
              <span
                className="badge bg-primary-subtle text-primary border border-primary-subtle flex-shrink-0"
                style={{ fontSize: "10px" }}
              >
                HOSPITAL
              </span>
            )}
          </div>

          <div className="d-flex align-items-center gap-2 mb-2 flex-nowrap overflow-hidden">
            <span className="text-muted small text-truncate-custom flex-grow-1" style={{ fontSize: "12px", minWidth: 0 }}>
              <i className="bi bi-geo-alt-fill me-1"></i>{data.city}, {data.state}
            </span>
            {vendorProfileUrl(data) && (
              <Link to={vendorProfileUrl(data)} className="badge bg-light text-dark text-decoration-none flex-shrink-0"
                title="View Vendor Profile" aria-label="View Vendor Profile" onClick={(event) => event.stopPropagation()}>
                <i className="bi bi-shop" style={{ color: '#0095D3', fontSize: '14px' }}></i>
              </Link>
            )}
          </div>

          <div className="mb-2">
            <span
              className="text-uppercase fw-bold text-secondary"
              style={{ fontSize: "10px", letterSpacing: "1px" }}
            >
              Speciality
            </span>
            <div
              className="text-dark text-truncate-custom"
              style={{ fontSize: "14px" }}
            >
              {data.speciality_names || "General Healthcare"}
            </div>
          </div>

          {data.consultation_fees && (
            <div className="mb-2">
              <span
                className="text-uppercase fw-bold text-secondary"
                style={{ fontSize: "10px", letterSpacing: "1px" }}
              >
                Consultation Fee
              </span>
              <div className="fw-bold fs-5" style={{ color: successColor }}>
                ₹{data.consultation_fees}
              </div>
            </div>
          )}

          <div className="mt-2">
            <span
              className="badge rounded-pill px-3 py-2 shadow-sm text-truncate-custom"
              style={{
                backgroundColor: "#188cbd",
                color: "white",
                fontSize: "16px",
                fontWeight: "600",
                maxWidth: "100%",
                display: "inline-block",
              }}
            >
              <span className="me-2">🕒</span>{" "}
              {getFormattedAvailability(data.availability)}
            </span>
          </div>
        </div>

        {/* ACTION BUTTONS */}
        <div className="col-12 col-lg-3 p-3 bg-light-subtle border-start-lg border-top-md-0 border-top d-flex flex-column justify-content-center medical-card-actions">
          <div className="d-grid gap-2">
            {/* ⭐ REVIEW SECTION */}
            <div className="review-section d-none d-lg-block mb-2 pb-2 border-bottom text-center text-lg-start">
              <StarRating
                avg={avgRating}
                count={totalReviews}
                onClick={(e) => {
                  e.stopPropagation();
                  onReviewClick && onReviewClick(data, type);
                }}
              />
            </div>

            <div className="review-section d-none d-md-block d-lg-none mb-2 pb-2 border-bottom text-center">
              <StarRating
                avg={avgRating}
                count={totalReviews}
                onClick={(e) => {
                  e.stopPropagation();
                  onReviewClick && onReviewClick(data, type);
                }}
              />
            </div>

            {/* Mobile: review summary left, call/WhatsApp icons right */}
            <div className="medical-mobile-action-row d-md-none">
              <div className="medical-mobile-rating">
                <StarRating
                  avg={avgRating}
                  count={totalReviews}
                  onClick={(e) => {
                    e.stopPropagation();
                    onReviewClick && onReviewClick(data, type);
                  }}
                />
              </div>
              <div className="medical-mobile-icons">
                <button
                  className="medical-mobile-icon-btn"
                  onClick={(e) => onAction(e, "call", data, type)}
                  disabled={isSubmitting}
                  title="Call Now"
                >
                  {isActive && pendingAction === "call" ? (
                    <span className="spinner-border spinner-border-sm"></span>
                  ) : (
                    <img src="/assets/img/mobile-icon/call.png" alt="Call" />
                  )}
                </button>
                <button
                  className="medical-mobile-icon-btn"
                  onClick={(e) => onAction(e, "whatsapp", data, type)}
                  disabled={isSubmitting}
                  title="Contact Owner"
                >
                  {isActive && pendingAction === "whatsapp" ? (
                    <span className="spinner-border spinner-border-sm"></span>
                  ) : (
                    <img src="/assets/img/mobile-icon/whatsapp.png" alt="WhatsApp" />
                  )}
                </button>
              </div>
            </div>

            {/* Tablet: compact icon-only buttons in a row */}
            <div className="d-none d-md-flex d-lg-none gap-2 justify-content-center">
              <button
                className="btn btn-outline-primary fw-bold rounded-3 d-flex align-items-center justify-content-center"
                style={{ width: '52px', height: '44px', flexShrink: 0, cursor: 'pointer' }}
                onClick={(e) => onAction(e, "call", data, type)}
                disabled={isSubmitting}
                title="Call Now"
              >
                {isActive && pendingAction === "call" ? (
                  <span className="spinner-border spinner-border-sm"></span>
                ) : (
                  <i className="bi bi-telephone-fill"></i>
                )}
              </button>
              <button
                className="btn btn-outline-success fw-bold rounded-3 d-flex align-items-center justify-content-center"
                style={{ width: '52px', height: '44px', flexShrink: 0, cursor: 'pointer' }}
                onClick={(e) => onAction(e, "whatsapp", data, type)}
                disabled={isSubmitting}
                title="Contact Owner"
              >
                {isActive && pendingAction === "whatsapp" ? (
                  <span className="spinner-border spinner-border-sm"></span>
                ) : (
                  <i className="bi bi-whatsapp"></i>
                )}
              </button>
              <button
                className="btn btn-outline-dark fw-bold rounded-3 d-flex align-items-center justify-content-center listing-book-btn"
                style={{ width: '52px', height: '44px', flexShrink: 0, cursor: 'pointer' }}
                onClick={(e) => onAction(e, "book", data, type)}
                disabled={isSubmitting}
                title="Book Now"
              >
                {isActive && pendingAction === "book" ? (
                  <span className="spinner-border spinner-border-sm"></span>
                ) : (
                  <i className="bi bi-calendar-check-fill"></i>
                )}
              </button>
            </div>

            {/* Desktop: stacked full-width buttons */}
            <div className="d-none d-lg-flex flex-column gap-2">
              <button
                className="btn btn-outline-primary fw-bold py-2 rounded-3 text-truncate d-flex align-items-center justify-content-center w-100"
                style={{ cursor: 'pointer' }}
                onClick={(e) => onAction(e, "call", data, type)}
                disabled={isSubmitting}
              >
                {isActive && pendingAction === "call" ? (
                  <span className="spinner-border spinner-border-sm me-2"></span>
                ) : (
                  <i className="bi bi-telephone-fill me-2"></i>
                )}
                {isActive && pendingAction === "call" ? "Connecting..." : "Call Now"}
              </button>

              <button
                className="btn btn-outline-success fw-bold py-2 rounded-3 text-truncate d-flex align-items-center justify-content-center w-100"
                style={{ cursor: 'pointer' }}
                onClick={(e) => onAction(e, "whatsapp", data, type)}
                disabled={isSubmitting}
              >
                {isActive && pendingAction === "whatsapp" ? (
                  <span className="spinner-border spinner-border-sm me-2"></span>
                ) : (
                  <i className="bi bi-whatsapp me-2"></i>
                )}
                {isActive && pendingAction === "whatsapp" ? "Connecting..." : "WhatsApp Now"}
              </button>

              <button
                className="btn btn-outline-dark fw-bold py-2 rounded-3 text-truncate d-flex align-items-center justify-content-center w-100 listing-book-btn"
                style={{ cursor: 'pointer' }}
                onClick={(e) => onAction(e, "book", data, type)}
                disabled={isSubmitting}
              >
                {isActive && pendingAction === "book" ? (
                  <span className="spinner-border spinner-border-sm me-2"></span>
                ) : null}
                {isActive && pendingAction === "book" ? "Processing..." : "Book Now"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- RESPONSIVE SEARCH FORM ---

function SearchFormMedical({ onSearch }) {
  const [activeTab, setActiveTab] = useState("Doctors");
  const [cityLabel, setCityLabel] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [keyword, setKeyword] = useState("");
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);

  // Suggestion States
  const [suggestions, setSuggestions] = useState({
    doctors: [],
    specialities: [],
    hospitals: [],
  });
  const [filtered, setFiltered] = useState({
    doctors: [],
    specialities: [],
    hospitals: [],
  });
  const [isResultsOpen, setIsResultsOpen] = useState(false);

  const cityAutoRef = useRef(null);
  const dropdownRef = useRef(null);

  // Load Google Maps Script
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAP_KEY,
    libraries,
  });

  // 1. Fetch Suggestions Data from your API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [docRes, specRes, hospRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/doctors/public/getdoctors`),
          axios.get(`${process.env.REACT_APP_API_URL}/doctors/public/specialities`),
          axios.get(`${process.env.REACT_APP_API_URL}/hospitals/public/gethospitals`),
        ]);
        setSuggestions({
          doctors: docRes.data?.data || [],
          specialities: specRes.data?.data || [],
          hospitals: hospRes.data?.data || [],
        });
      } catch (err) {
        console.error("Data Load Error:", err);
      }
    };
    fetchData();
  }, []);

  // 2. Filter Suggestions based on keyword
  useEffect(() => {
    const term = keyword.toLowerCase().trim();
    if (!term) {
      setFiltered({
        doctors: [],
        specialities: suggestions.specialities.slice(0, 5),
        hospitals: [],
      });
      return;
    }
    setFiltered({
      doctors: suggestions.doctors.filter((d) =>
        d.doctor_name.toLowerCase().includes(term),
      ),
      specialities: suggestions.specialities.filter((s) =>
        s.speciality_name.toLowerCase().includes(term),
      ),
      hospitals: suggestions.hospitals.filter((h) =>
        h.hospital_name.toLowerCase().includes(term),
      ),
    });
  }, [keyword, suggestions]);

  const getLocationSelection = (place = {}) => {
    const components = place.address_components || [];
    const getComponent = (...types) => {
      const component = components.find((item) =>
        types.some((type) => item.types?.includes(type)),
      );
      return component?.long_name || "";
    };

    const city =
      getComponent("locality") ||
      getComponent("administrative_area_level_3") ||
      getComponent("administrative_area_level_2") ||
      getComponent("administrative_area_level_1");

    const area =
      getComponent("sublocality_level_1") ||
      getComponent("sublocality") ||
      getComponent("neighborhood") ||
      (place.name && place.name !== city ? place.name : "");

    const displayParts = [area, city].filter(
      (part, index, arr) => part && arr.indexOf(part) === index,
    );
    const fallbackLabel = place.formatted_address?.split(",").slice(0, 2).join(", ").trim() || "";

    return {
      filterCity: city || area || fallbackLabel,
      displayLabel: displayParts.join(", ") || fallbackLabel || city || area,
    };
  };

  // 3. DETECT LOCATION LOGIC (Coordinates to Area + City)
  const handleDetectLocation = () => {
    if (!isLoaded || !window.google) {
      toast.info("Maps is still loading. Please try again in a moment.");
      return;
    }

    if (!navigator.geolocation) {
      toast.error("Location detection is not supported in this browser.");
      return;
    }

    setIsDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const geocoder = new window.google.maps.Geocoder();

        geocoder.geocode(
          { location: { lat: latitude, lng: longitude } },
          (results, status) => {
            setIsDetectingLocation(false);

            if (status === "OK" && results[0]) {
              const { filterCity, displayLabel } = getLocationSelection(results[0]);

              if (filterCity) {
                setSelectedCity(filterCity);
                setCityLabel(displayLabel || filterCity);
                toast.success(`Location detected: ${displayLabel || filterCity}`);
                return;
              }
            }

            toast.error("Could not detect your city from the current location.");
          },
        );
      },
      (error) => {
        setIsDetectingLocation(false);
        const message =
          error.code === error.PERMISSION_DENIED
            ? "Please enable location permissions in your browser."
            : "Unable to detect your current location. Please try again.";
        toast.error(message);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 },
    );
  };

  // 4. Handle Google Maps Autocomplete Selection
  const onPlaceChanged = () => {
    if (cityAutoRef.current !== null) {
      const place = cityAutoRef.current.getPlace();
      const { filterCity, displayLabel } = getLocationSelection(place);

      if (filterCity) {
        setSelectedCity(filterCity);
        setCityLabel(displayLabel || filterCity);
      }
    }
  };

  // Close dropdown on click outside
  useEffect(() => {
    const clickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setIsResultsOpen(false);
    };
    document.addEventListener("mousedown", clickOutside);
    return () => document.removeEventListener("mousedown", clickOutside);
  }, []);

  if (loadError)
    return <div className="p-3 text-danger">Error loading Maps API</div>;

  return (
    <div className="search-form-card shadow-lg" ref={dropdownRef}>
      {/* TABS */}
      <div className="d-flex bg-light rounded-top-4 overflow-hidden border-bottom">
        {["Doctors", "Hospitals"].map((tab) => (
          <div
            key={tab}
            className={`search-tab-item ${activeTab === tab ? "is-active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </div>
        ))}
      </div>

      <form
        className="p-2"
        onSubmit={(e) => {
          e.preventDefault();
          setIsResultsOpen(false);
          onSearch(selectedCity || cityLabel, keyword);
        }}
      >
        <div className="d-flex flex-column flex-md-row align-items-stretch">
          {/* LOCATION INPUT (Google API) */}
          <div className="input-field-wrap border-end-md">
            <span className="field-icon">📍</span>
            <div
              className="flex-grow-1 overflow-hidden"
              style={{ minWidth: 0 }}
            >
              {isLoaded ? (
                <Autocomplete
                  onLoad={(autocomplete) =>
                    (cityAutoRef.current = autocomplete)
                  }
                  onPlaceChanged={onPlaceChanged}
                >
                  <input
                    type="text"
                    className={`input-clean text-truncate-custom ${cityLabel ? "filter-value-active" : ""}`}
                    placeholder="Select Location..."
                    value={cityLabel}
                    onChange={(e) => {
                      setCityLabel(e.target.value);
                      setSelectedCity("");
                    }}
                  />
                </Autocomplete>
              ) : (
                <input
                  type="text"
                  className="input-clean"
                  placeholder="Loading Maps..."
                  disabled
                />
              )}
            </div>
            <button
              type="button"
              onClick={handleDetectLocation}
              className="btn-location-detect"
              disabled={isDetectingLocation || !isLoaded}
              title="Use current location"
            >
              {isDetectingLocation ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                "🎯"
              )}
            </button>
          </div>

          {/* KEYWORD INPUT (Speciality/Doctor/Hospital) */}
          <div
            className="input-field-wrap position-relative flex-grow-1"
            style={{ minWidth: 0 }}
          >
            <span className="field-icon">🔍</span>
            <input
              type="text"
              className={`input-clean text-truncate-custom ${keyword ? "filter-value-active" : ""}`}
              placeholder={
                activeTab === "Doctors"
                  ? "Speciality or Doctor or Hospital..."
                  : "Hospital Name..."
              }
              value={keyword}
              onFocus={() => setIsResultsOpen(true)}
              onChange={(e) => {
                setKeyword(e.target.value);
                setIsResultsOpen(true);
              }}
            />

            {/* SUGGESTION DROPDOWN */}
            {isResultsOpen && (
              <div className="anchored-dropdown animate-fade shadow-lg">
                <div className="dropdown-label-text">Quick Suggestions</div>
                <div className="dropdown-scroll-container">
                  {filtered.specialities.map((i) => (
                    <div
                      key={`s-${i.id}`}
                      className="dropdown-result-row"
                      onClick={() => {
                        setKeyword(i.speciality_name);
                        setIsResultsOpen(false);
                      }}
                    >
                      <div className="row-icon speciality-icon">🩺</div>
                      <div className="row-info overflow-hidden">
                        <div className="row-title text-truncate-custom">
                          {i.speciality_name}
                        </div>
                        <div className="row-tag">Speciality</div>
                      </div>
                    </div>
                  ))}
                  {filtered.doctors.map((i) => (
                    <div
                      key={`d-${i.id}`}
                      className="dropdown-result-row"
                      onClick={() => {
                        setKeyword(i.doctor_name);
                        setIsResultsOpen(false);
                      }}
                    >
                      <div className="row-img-wrap">
                        <img
                          src={i.doctor_images?.[0]?.image_url || "/assets/img/placeholders/doctor.png"}
                          alt="dr"
                          onError={(e) => {
                            e.target.src = "/assets/img/placeholders/doctor.png";
                          }}
                        />
                      </div>
                      <div className="row-info overflow-hidden">
                        <div className="row-title text-truncate-custom">
                          {i.doctor_name}
                        </div>
                        <div className="row-tag">Doctor</div>
                      </div>
                    </div>
                  ))}
                  {filtered.hospitals.map((i) => (
                    <div
                      key={`h-${i.id}`}
                      className="dropdown-result-row"
                      onClick={() => {
                        setKeyword(i.hospital_name);
                        setIsResultsOpen(false);
                      }}
                    >
                      <div className="row-icon bg-info-subtle text-info">
                        🏥
                      </div>
                      <div className="row-info overflow-hidden">
                        <div className="row-title text-truncate-custom">
                          {i.hospital_name}
                        </div>
                        <div className="row-tag">Hospital</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button type="submit" className="btn btn-primary main-search-btn">
            Search
          </button>
        </div>
      </form>

      <style>{`
        .search-form-card { background: white; border-radius: 16px; max-width: 850px; margin: 0 auto; position: relative; z-index: 1050; }
        .search-tab-item { flex: 1; padding: 14px; text-align: center; font-weight: 500; color: #7f8c8d; cursor: pointer; font-size: 14px; }
        .search-tab-item.is-active { color: #0d6efd; background: white; border-bottom: 3px solid #0d6efd; }
        .input-field-wrap { display: flex; align-items: center; padding: 12px 18px; flex: 1; text-align: left; overflow: visible; }
        .field-icon { font-size: 18px; margin-right: 12px; opacity: 0.8; flex-shrink: 0; }
        .input-clean { border: none; outline: none; width: 100%; font-size: 15px; font-weight: 500; color: #2c3e50; background: transparent; }
        .input-clean.filter-value-active { color: #111827 !important; font-weight: 500; }
        .input-clean::placeholder { color: #6c757d; opacity: 1; }
        .text-truncate-custom { display: block; white-space: nowrap;
         overflow: hidden; text-overflow: ellipsis; width: 100%; font-size:18px; }

        .btn-location-detect { border: none; background: transparent; cursor: pointer; font-size: 18px; flex-shrink: 0; transition: 0.2s; }
        .btn-location-detect:hover { transform: scale(1.2); }
        .main-search-btn { border-radius: 12px; font-weight: 500; padding: 0 30px; min-height: 50px; text-transform: uppercase; margin-left: 5px; }

        /* SUGGESTIONS */
        .anchored-dropdown { position: absolute; top: 105%; left: 0; width: 100%; background: #ffffff; border-radius: 12px; border: 1px solid #e2e8f0; border-left: 5px solid #0d6efd; z-index: 3000; text-align: left; }
        .dropdown-label-text { background: #f8fafc; padding: 8px 18px; font-size: 10px; font-weight: 500; color: #94a3b8; text-transform: uppercase; }
        .dropdown-scroll-container { max-height: 280px; overflow-y: auto; }
        .dropdown-result-row { display: flex; align-items: center; padding: 10px 18px; cursor: pointer; border-bottom: 1px solid #f1f5f9; }
        .dropdown-result-row:hover { background-color: #f0f7ff; }
        .row-icon { width: 36px; height: 36px; border-radius: 8px; display: flex; align-items: center; justify-content: center; margin-right: 14px; flex-shrink: 0; font-size: 16px; }
        .speciality-icon { background: #e0f2fe; color: #0369a1; }
        .row-img-wrap { width: 36px; height: 36px; border-radius: 8px; overflow: hidden; margin-right: 14px; flex-shrink: 0; border: 1px solid #e2e8f0; }
        .row-img-wrap img { width: 100%; height: 100%; object-fit: cover; }
        .row-info { text-align: left; flex: 1; min-width: 0; }
        .row-title { font-size: 14px; font-weight: 500; color: #1e293b; }
        .row-tag { font-size: 11px; color: #64748b; }

        /* Google Maps Dropdown Fix */
        .pac-container { border-radius: 12px !important; border-left: 5px solid #0d6efd !important; border-top: 1px solid #eee !important; box-shadow: 0 10px 25px rgba(0,0,0,0.15) !important; z-index: 4000 !important; }

        @media (max-width: 767px) {
            .input-field-wrap { border-bottom: 1px solid #f1f5f9; }
            .border-end-md { border-right: none !important; }
            .main-search-btn { width: 100%; margin: 5px 0; }
        }
      `}</style>
    </div>
  );
}

export default function MedicalTourism() {
  const { pageContent, getSection, isSectionActive, loading: pageContentLoading } = useFrontendPageContent('medical-tourism');
  const heroSection = getSection('hero', {
    title: 'Find the Best Healthcare',
    subtitle: 'Access top-rated verified Doctors and Hospitals instantly.',
    background_image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?q=80&w=2053&auto=format&fit=crop',
  });
  const resultsSection = getSection('results', {
    title: 'Available Healthcare',
  });
  const [isLoginPopupOpen, setIsLoginPopupOpen] = useState(false);
  const [results, setResults] = useState({ doctors: [], hospitals: [] });
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  // --- LEAD GENERATION STATES ---
  const [showThanksModal, setShowThanksModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeEntity, setActiveEntity] = useState(null);
  const [user, setUser] = useState(() => {
    const session = getValidSession();
    return session?.user?.user || session?.user || null;
  });

  // --- REVIEW MODAL STATES ---
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [selectedEntityForReview, setSelectedEntityForReview] = useState(null);
  const [reviewEntityType, setReviewEntityType] = useState("");
  const [reviewApiEndpoint, setReviewApiEndpoint] = useState("");
  const [pendingReviewAfterLogin, setPendingReviewAfterLogin] = useState(false);

  const handleReviewClick = (entity, entityType) => {
    setSelectedEntityForReview(entity);
    setReviewEntityType(entityType);
    setReviewApiEndpoint(entityType === "doctor" ? "/doctors" : "/hospitals");
    setReviewModalOpen(true);
  };

  const handleReviewLoginRequired = () => {
    setPendingReviewAfterLogin(true);
    setReviewModalOpen(false);
    setIsLoginPopupOpen(true);
  };

  // Sync user state if session changes
  useEffect(() => {
    const session = getValidSession();
    setUser(session?.user?.user || session?.user || null);
  }, [isLoginPopupOpen]);

  // --- PAGINATION STATES ---
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastSearchParams, setLastSearchParams] = useState({ city: "", keyword: "" });

  const handleAction = (e, type, entityData, entityType) => {
    e.preventDefault();
    e.stopPropagation();

    if (isSubmitting) return;

    setActiveEntity({ data: entityData, type: entityType });
    setPendingAction(type);

    if (!user) {
      setIsLoginPopupOpen(true);
      return;
    }

    processLead(type, user, { data: entityData, type: entityType });
  };

  const handleLoginSuccess = (loggedInUser) => {
    const session = getValidSession();
    const resolvedUser = session?.user?.user || session?.user || loggedInUser;
    setUser(resolvedUser);
    setIsLoginPopupOpen(false);

    if (pendingReviewAfterLogin && selectedEntityForReview) {
      setPendingReviewAfterLogin(false);
      setReviewModalOpen(true);
      return;
    }

    if (pendingAction && activeEntity) {
      processLead(pendingAction, resolvedUser, activeEntity);
    }
  };

  const processLead = async (actionType, userData, entity) => {
    setIsSubmitting(true);
    const currentUser = userData || user;
    const { data, type } = entity;

    const payload = {
      name: currentUser?.name || "Guest",
      email: currentUser?.email || "",
      mobile: (currentUser?.phone || currentUser?.mobile || "").replace(/^(\+91|91)/, ""),
      type: type, // 'doctor' or 'hospital'
      type_id: data.id,
      message: `User clicked ${actionType} for ${type === 'doctor' ? data.doctor_name : data.hospital_name}`
    };

    let success = false;
    try {
      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
      const res = await axios.post(`${API_URL}/enquiries/create`, payload);
      if (res.data.success) {
        success = true;
        setShowThanksModal(true);
        setTimeout(() => {
          handleFinalRedirect(actionType, data);
        }, 2500);
      }
    } catch (err) {
      toast.error("Error processing request. Please try again.");
      console.error(err);
    } finally {
      setIsSubmitting(false);
      if (!success) {
        setPendingAction(null);
      }
    }
  };

  const handleFinalRedirect = (actionType, data) => {
    setShowThanksModal(false);
    const contactNumber = data.phone_number || data.whatsapp_number || data.hospital_phone_number || data.hospital_whatsapp_number;
    const email = data.doctor_email || data.hospital_email || data.email;

    if (actionType === "call") {
      window.location.href = `tel:${contactNumber}`;
    } else if (actionType === "whatsapp") {
      window.open(`https://wa.me/${contactNumber}`, "_blank");
    } else if (actionType === "email") {
      window.location.href = `mailto:${email}`;
    } else if (actionType === "book" && activeEntity) {
      toast.info("Proceeding to booking...");
      const url = `/medical-details/${activeEntity.type}/${generateMedicalSlug(data, activeEntity.type)}`;
      window.open(url, '_blank');
    }
    setPendingAction(null);
    setActiveEntity(null);
  };

  useEffect(() => {
    handleSearchAction("", "", 1, false);
  }, []);

  const handleSearchAction = async (city, keyword, pageNum = 1, isLoadMore = false) => {
    if (!isLoadMore) {
      setLoading(true);
      setResults({ doctors: [], hospitals: [] });
    } else {
      setLoadingMore(true);
    }

    setHasSearched(true);
    setLastSearchParams({ city, keyword });

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/hospitals/public/search?city=${city}&keyword=${keyword}&page=${pageNum}&limit=20`,
      );
      console.log("Search Result:", res.data);
      if (res.data.success) {
        if (isLoadMore) {
          setResults((prev) => ({
            doctors: [...prev.doctors, ...res.data.data.doctors],
            hospitals: [...prev.hospitals, ...res.data.data.hospitals],
          }));
        } else {
          setResults(res.data.data);
        }
        setHasMore(res.data.hasMore);
        setPage(pageNum);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    handleSearchAction(lastSearchParams.city, lastSearchParams.keyword, page + 1, true);
  };

  if (pageContentLoading || !pageContent) {
    return <Preloader />;
  }

  return (
    <div className="medical-tourism-wrapper bg-light min-vh-100">
      <FrontendPageSEO
        page={pageContent}
        fallbackTitle="Medical Tourism & Healthcare Services | Mangoloops"
        fallbackDescription="Find top-rated healthcare services with Mangoloops. Access verified doctors and hospitals for your medical needs."
        fallbackSlug="/medicaltourism"
      />
      <Header onLoginClick={() => setIsLoginPopupOpen(true)} />
      <LoginPopup
        isOpen={isLoginPopupOpen}
        onClose={() => {
          setIsLoginPopupOpen(false);
          setIsSubmitting(false);
          setPendingAction(null);
          setPendingReviewAfterLogin(false);
        }}
        onLoginSuccess={handleLoginSuccess}
        serviceType={activeEntity?.type === 'doctor' ? 'Doctor' : 'Hospital'}
      />
      {isSectionActive('hero') && (
        <div
          className="banner-content text-center px-3"
          style={{
            background:
              `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url("${heroSection.background_image}")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            padding: "80px 0 100px 0",
          }}
        >
          <div className="container">
            <h1 className="display-5 fw-bold text-white mb-3">
              {heroSection.title}
            </h1>
            <p className="fs-6 text-white opacity-75 mb-5">
              {heroSection.subtitle}
            </p>
            <SearchFormMedical onSearch={(city, keyword) => handleSearchAction(city, keyword, 1, false)} />
          </div>
        </div>
      )}

      {isSectionActive('results') && (
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-12 col-xl-12">
              {hasSearched ? (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h3 className="fw-bold mb-0 text-start" style={{ fontSize: "90%" }}>
                      {loading ? "Searching..." : "Available Healthcare"}
                    </h3>
                    {!loading && (
                      <span className="text-muted small">
                        Showing{" "}
                        {results.doctors?.length + results.hospitals?.length}{" "}
                        results
                      </span>
                    )}
                  </div>
                  {results.doctors?.map((doc) => (
                    <MedicalResultCard
                      key={`doc-${doc.id}`}
                      data={doc}
                      type="doctor"
                      onAction={handleAction}
                      onReviewClick={handleReviewClick}
                      isSubmitting={isSubmitting}
                      pendingAction={pendingAction}
                      activeId={activeEntity?.data?.id}
                    />
                  ))}
                  {results.hospitals?.map((hosp) => (
                    <MedicalResultCard
                      key={`hosp-${hosp.id}`}
                      data={hosp}
                      type="hospital"
                      onAction={handleAction}
                      onReviewClick={handleReviewClick}
                      isSubmitting={isSubmitting}
                      pendingAction={pendingAction}
                      activeId={activeEntity?.data?.id}
                    />
                  ))}

                  {/* LOAD MORE BUTTON */}
                  {hasMore && (
                    <div className="text-center mt-4 mb-5">
                      <button
                        className="btn btn-primary px-5 py-2 fw-bold rounded-pill shadow-sm"
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        style={{ backgroundColor: '#32AEE4', borderColor: '#32AEE4' }}
                      >
                        {loadingMore ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Loading More...
                          </>
                        ) : (
                          "Load More"
                        )}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-5">
                  <h2 className="fw-bold mb-3 text-primary">
                    Your Health, Our Priority
                  </h2>
                  <p className="text-muted mx-auto" style={{ maxWidth: "600px" }}>
                    Search by location or speciality to find trusted experts.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <Footer />

      {/* --- THANK YOU MODAL --- */}
      {(isSubmitting || showThanksModal) && !isLoginPopupOpen && (
        <div className="custom-modal-overlay" style={{ zIndex: 9999999 }}>
          <div className="thanks-modal card shadow-lg border-0 rounded-4 text-center p-5" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            {isSubmitting && !showThanksModal ? (
              <>
                <div className="mb-4">
                  <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem', borderWidth: '0.3em' }}>
                    <span className="visually-hidden">Processing...</span>
                  </div>
                </div>
                <h4 className="fw-bold mb-2">Processing your request...</h4>
                <p className="text-muted mb-0">Please wait while we save your lead and notify the vendor.</p>
              </>
            ) : (
              <>
                <div className="mb-4">
                  <div className="success-checkmark-wrap">
                    <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
                  </div>
                </div>
                <h2 className="fw-bold mb-3">Thank You!</h2>
                <p className="text-muted mb-4">Your request has been sent to the team. We will get back to you shortly.</p>
                <button className="btn btn-primary rounded-pill px-4 py-2 fw-bold" onClick={() => handleFinalRedirect(pendingAction, activeEntity.data)}>
                  OK
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* --- REVIEW MODAL --- */}
      {reviewModalOpen && (
        <ReviewModal
          isOpen={reviewModalOpen}
          onClose={() => setReviewModalOpen(false)}
          entity={selectedEntityForReview}
          entityType={reviewEntityType}
          user={user}
          apiEndpoint={reviewApiEndpoint}
          onLoginRequired={handleReviewLoginRequired}
        />
      )}

      <style>{`
        @media (min-width: 992px) { .border-start-lg { border-left: 1px solid #dee2e6 !important; } }
        .result-card-hover:hover { transform: translateY(-3px); box-shadow: 0 12px 25px rgba(0,0,0,0.08) !important; transition: 0.3s; }
        .text-truncate-custom { display: block; white-space: nowrap; overflow: hidden; 
        text-overflow: ellipsis; }

        /* Book Now button: transparent default, black on hover */
        .listing-book-btn {
            background: transparent !important;
            color: #212529 !important;
            border: 1px solid #212529 !important;
            cursor: pointer !important;
            transition: all 0.3s ease !important;
        }
        .listing-book-btn:hover {
            background: #212529 !important;
            color: #fff !important;
            border-color: #212529 !important;
        }

        .custom-modal-overlay {
          position: fixed; top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center;
          z-index: 999999; padding: 15px;
          animation: fadeIn 0.2s ease;
        }
        .thanks-modal {
          animation: slideUp 0.3s ease;
          background: white;
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

        /* Custom Spinner */
        .spinner-border {
          display: inline-block;
          width: 1rem;
          height: 1rem;
          vertical-align: text-bottom;
          border: 0.2em solid currentColor;
          border-right-color: transparent;
          border-radius: 50%;
          animation: spinner-border .75s linear infinite;
        }
        @keyframes spinner-border {
          to { transform: rotate(360deg); }
        }
        .spinner-border-sm {
          width: 1rem;
          height: 1rem;
          border-width: 0.2em;
        }
        .medical-mobile-action-row {
          align-items: center;
          display: flex;
          gap: 10px;
          justify-content: space-between;
          width: 100%;
        }
        .medical-mobile-rating {
          flex: 1 1 auto;
          min-width: 0;
        }
        .medical-mobile-rating > .d-flex {
          flex-wrap: nowrap !important;
          gap: 6px !important;
          justify-content: flex-start !important;
          white-space: nowrap;
        }
        .medical-mobile-rating i {
          font-size: 14px !important;
        }
        .medical-mobile-rating .fw-bold {
          font-size: 13px !important;
        }
        .medical-mobile-rating .text-muted {
          font-size: 11px !important;
        }
        .medical-mobile-icons {
          align-items: center;
          display: flex;
          flex: 0 0 auto;
          gap: 8px;
        }
        .medical-mobile-icon-btn {
          align-items: center;
          background: transparent;
          border: 0;
          cursor: pointer;
          display: inline-flex;
          height: 32px;
          justify-content: center;
          padding: 0;
          width: 32px;
        }
        .medical-mobile-icon-btn:hover {
          opacity: 0.85;
        }
        .medical-mobile-icon-btn:disabled {
          cursor: not-allowed;
          opacity: 0.7;
        }
        .medical-mobile-icon-btn img {
          display: block;
          height: 27px;
          object-fit: contain;
          width: 27px;
        }
        @media (max-width: 575.98px) {
          .medical-card-actions {
            padding: 10px 16px !important;
          }
        }
        @media (max-width: 360px) {
          .medical-mobile-action-row {
            gap: 8px;
          }
          .medical-mobile-icons {
            gap: 6px;
          }
          .medical-mobile-icon-btn {
            height: 30px;
            width: 30px;
          }
          .medical-mobile-icon-btn img {
            height: 20px;
            width: 20px;
          }
          .medical-mobile-rating i {
            font-size: 12px !important;
          }
          .medical-mobile-rating .fw-bold {
            font-size: 12px !important;
          }
          .medical-mobile-rating .text-muted {
            font-size: 10px !important;
          }
        }
      `}</style>
    </div>
  );
}
